import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, GraduationCap, BookOpen, FileEdit, Languages, Plane, CheckCircle2, Circle, Clock } from "lucide-react";
import { http } from "@/lib/api";
import { clearPortalSession, portalHttp } from "@/lib/portalAuth";

const STAGES = [
  { key: "shortlist", label: "University shortlist",       Icon: GraduationCap, done: true },
  { key: "sop",       label: "Statement of Purpose draft", Icon: FileEdit,      done: true, active: false },
  { key: "ielts",     label: "IELTS / English score",      Icon: Languages,     done: false, active: true, value: "7.0 target" },
  { key: "offers",    label: "Offer letters",              Icon: BookOpen,      done: false },
  { key: "visa",      label: "Student visa approval",      Icon: Plane,         done: false },
];

export default function StudentDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalHttp.get("/portal/me")
      .then(({ data }) => setMe(data))
      .catch((err) => {
        console.error("Portal /me failed:", err);
        nav("/student/login");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const logout = async () => {
    try { await http.post("/portal/logout"); } catch (e) {}
    clearPortalSession();
    toast.success("Signed out");
    nav("/student/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!me) return null;

  return (
    <div data-testid="student-dashboard" className="min-h-screen bg-[#F4F6F9] text-[#0F172A]">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="NS-I" className="h-9 w-auto" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Student portal</div>
            <div className="font-serif text-lg">Hello, {me.name || me.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {me.picture && <img src={me.picture} alt="" className="w-8 h-8 rounded-full" />}
          <button data-testid="student-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#C9A961]">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[["Target", "Masters · UK"], ["Intake", "Fall 2026"], ["Shortlisted unis", "6"], ["IELTS target", "7.0"]].map(([l, v]) => (
            <div key={l}>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">{l}</div>
              <div className="font-serif text-lg mt-1">{v}</div>
            </div>
          ))}
        </section>

        <section className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-5">Journey</div>
          <ol className="space-y-5">
            {STAGES.map((s) => (
              <li key={s.key} data-testid={`student-milestone-${s.key}`} className="flex items-start gap-4">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-100 text-emerald-700" : s.active ? "bg-[#C9A961]/20 text-[#8B6F30]" : "bg-slate-100 text-slate-400"}`}>
                  {s.done ? <CheckCircle2 className="w-5 h-5" /> : s.active ? <Clock className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${s.active ? "text-[#0B1E3F]" : s.done ? "text-slate-700" : "text-slate-400"}`}>{s.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.done ? "Completed" : s.active ? `In progress ${s.value ? "· " + s.value : ""}` : "Pending"}
                  </div>
                </div>
                <s.Icon className={`w-4 h-4 ${s.active ? "text-[#C9A961]" : "text-slate-300"}`} />
              </li>
            ))}
          </ol>
          <p className="mt-6 text-[11px] text-slate-400">Demo data · personalised journey coming soon.</p>
        </section>
      </main>
    </div>
  );
}
